import * as React from "react";
import { useEffect } from "react";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import { Grid } from "@mui/material";
import { useParams } from "react-router-dom";
import RefreshIcon from "@mui/icons-material/Refresh";
import Stack from "@mui/material/Stack";
import NfcIcon from '@mui/icons-material/Nfc';
import {
  useGetSmartNodeStatusQuery,
  useEditJobCardMutation,
} from "../api/apiSlice";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import NavbarStepper from "./Stepper/NavBarStepper";
import theme from "../../Global/theme";
import CircleIcon from "@mui/icons-material/Circle";
import { useComprehensiveConnectionTest, useConnectionMonitor, getConnectionQualityColor, getConnectionQualityText, CONNECTION_QUALITY } from "./connectionsTest";
import LoadingOverlay from "./LoadingOverlay";
import { useStepperStateRestoration } from "./useStepperStateRestoration";
import AbortJobButton from "./AbortJobButton";

export default function ConnectionTestPage() {
  // Stepper state restoration
  useStepperStateRestoration();

  let { serial_number, imsi_number } = useParams();

  const QueryParams = React.useMemo(() => ({ serial_number }), [serial_number]);
  const {
    data: smart_node_data,
    isSuccess: isGetSmartNodeSuccess,
    refetch: refetchSmartNodeData,
  } = useGetSmartNodeStatusQuery(QueryParams);

  // Add API connection monitoring
  const {
    connectionQuality,
    responseTime,
    avgResponseTime,
    isWeakConnection
  } = useComprehensiveConnectionTest();

  const { isConnected } = useConnectionMonitor();

  const [showTroubleshooting, setShowTroubleshooting] = React.useState(false);
  const timerRef = React.useRef(null);

  const startTroubleshootTimer = React.useCallback(() => {
    clearTimeout(timerRef.current);
    setShowTroubleshooting(false);
    timerRef.current = setTimeout(() => setShowTroubleshooting(true), 30000);
  }, []);

  const handleRetry = React.useCallback(() => {
    startTroubleshootTimer();
    refetchSmartNodeData();
  }, [startTroubleshootTimer, refetchSmartNodeData]);

  React.useEffect(() => {
    startTroubleshootTimer();
    return () => clearTimeout(timerRef.current);
  }, [startTroubleshootTimer]);

  React.useEffect(() => {
    if (smart_node_data?.connected) {
      clearTimeout(timerRef.current);
      setShowTroubleshooting(false);
    }
  }, [smart_node_data?.connected]);

  if (isGetSmartNodeSuccess) {
    return (
      <>
        <NavbarStepper />

        <Grid container alignItems="center" justifyContent="center">
        <Grid item xs={1} align="left" />
          <Grid item xs={10} align="center"  sx={{ mb: 2 }}>
            <Typography
              variant="h8"
              color="grey"

            >
              Test Tjommie's connection.
            </Typography>
          </Grid>
          <Grid item xs={1} align="left" />
          <Grid item xs={10} align="center">
            <Box p={2} align="center" sx={{ borderRadius: 4, backgroundColor: "#161B22", border: "1px solid rgba(255,255,255,0.07)" }}>
              <Grid container>
                <Grid item xs={12} align="center">
                  <NfcIcon/>
                  <Typography sx={{ mt: 2, mb:2, fontSize: 18 }}>Tjommie</Typography>
                </Grid>
                <Grid item xs={1} />
                <Grid item xs={10} align="left">
                  <ConnectionStatus connected={smart_node_data.connected} />

                  <Typography sx={{ mt: 1 }}>SN: {serial_number}</Typography>
                  <Typography sx={{ mt: 1 }}>IMSI : {imsi_number}</Typography>
                  <Typography sx={{ mt: 1 }}>
                    Address: {smart_node_data.address}
                  </Typography>
                  {showTroubleshooting && !smart_node_data.connected && (
                    <TroubleshootingBanner />
                  )}
                </Grid>
                <Grid item xs={1} />

                <Grid item xs={1} />
                <Grid item xs={10} align="center">
                  <Button
                    variant="outlined"
                    style={theme.blackButtonStyle.root}
                    sx={{ mt: 2 }}
                    onClick={handleRetry}
                    endIcon={<RefreshIcon />}
                  >
                    Test Connection
                  </Button>
                </Grid>
                <Grid item xs={1} />
              </Grid>
            </Box>
          </Grid>

          <Grid item xs={12} sx={{ height: "80px" }} />
        </Grid>

        {/* Fixed bottom bar */}
        <Box sx={{
          position: "fixed", bottom: 0, left: 0, right: 0,
          display: "flex", gap: "12px",
          px: "16px", py: "14px",
          background: "rgba(13,14,20,0.95)",
          backdropFilter: "blur(12px)",
          borderTop: "1px solid rgba(255,255,255,0.06)",
        }}>
          <AbortJobButton />
          <NextButton connected={smart_node_data.connected} isSignalOk={isConnected} />
        </Box>
      </>
    );
  } else {
    return <></>;
  }
}

function ConnectionStatus({ connected }) {
  if (connected) {
    return (
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography>Tjommie Connected</Typography>
        <CircleIcon sx={{ color: "#119B05" }} />
      </Stack>
    );
  } else if (!connected) {
    return (
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography>Tjommie Not Connected</Typography>
        <CircleIcon sx={{ color: "red" }} />
      </Stack>
    );
  } else {
    return <Typography>Tjommie Status Unknown</Typography>;
  }
}

function TroubleshootingBanner() {
  const AMBER = "#FDB940";
  return (
    <Box sx={{
      mt: "16px",
      borderLeft: `3px solid ${AMBER}`,
      backgroundColor: "rgba(253,185,64,0.06)",
      borderRadius: "0 8px 8px 0",
      p: "12px 14px",
    }}>
      <Typography sx={{ color: AMBER, fontWeight: 700, fontSize: "0.82rem", mb: "8px" }}>
        Tjommie not connecting?
      </Typography>
      {[
        "Switch off the breaker, wait 5–10 seconds, then switch it back on.",
        "Check Tjommie's LED — it should be blue or flashing blue.",
        "Wait up to 2 minutes — Tjommie will reconnect automatically.",
      ].map((step, i) => (
        <Typography key={i} sx={{ color: "rgba(255,255,255,0.65)", fontSize: "0.78rem", lineHeight: 1.5, mb: "4px" }}>
          {i + 1}. {step}
        </Typography>
      ))}
    </Box>
  );
}

function ApiConnectionStatus({ connectionQuality, responseTime, avgResponseTime, isWeakConnection }) {
  const qualityColor = getConnectionQualityColor(connectionQuality);
  const qualityText = getConnectionQualityText(connectionQuality, responseTime);
  
  return (
    <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 1 }}>
      <Typography>API {qualityText}</Typography>
      <CircleIcon sx={{ color: qualityColor }} />
      {isWeakConnection && (
        <Typography variant="caption" color="warning.main" sx={{ ml: 1 }}>
          (Slow Connection)
        </Typography>
      )}
    </Stack>
  );
}

function NextButton({ connected, isSignalOk }) {

  let { serial_number, imsi_number } = useParams();
  let { job_card_reference_id } = useParams();
  let navigate = useNavigate();

  const [editJobCard, { isSuccess: isEditJobCardSuccess, isLoading: isEditJobCardLoading }] =
    useEditJobCardMutation();

  const handleConnectionTestCompleted = () => {
    const body = {
      reference_id: job_card_reference_id,
      state: "AFT",
    };
    editJobCard({ body: body });
  };

  useEffect(() => {
    if (isEditJobCardSuccess) {
      navigate(`/installer/${job_card_reference_id}/${serial_number}/${imsi_number}/after_install/`);
    }
  }, [isEditJobCardSuccess]);

  return (
    <Button
      variant="contained"
      disableElevation
      sx={{
        flex: 1,
        ...theme.yellowButtonStyle.root,
        border: "none",
        borderRadius: "10px",
        py: "13px",
        fontSize: "1rem",
        textTransform: "none",
        opacity: (!connected || !isSignalOk || isEditJobCardLoading) ? 0.55 : 1,
        "&.Mui-disabled": { opacity: 0.55 },
      }}
      onClick={handleConnectionTestCompleted}
      disabled={!connected || !isSignalOk || isEditJobCardLoading}
      startIcon={isEditJobCardLoading ? <CircularProgress size={20} color="inherit" /> : null}
    >
      {isEditJobCardLoading ? "Processing..." : "Next →"}
    </Button>
  );
}
